import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Calendar, Users, UserCheck, Clock, CheckCircle, XCircle, 
  AlertTriangle, Save, RefreshCw, Settings, BarChart3, Timer
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Student Attendance View Component
const StudentAttendanceView = ({ getAuthHeader }) => {
  const [attendanceData, setAttendanceData] = useState({ records: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    fetchMyAttendance();
  }, [selectedMonth]);

  const fetchMyAttendance = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/attendance/my-attendance?month=${selectedMonth}`, { 
        headers: getAuthHeader() 
      });
      setAttendanceData(res.data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const summary = attendanceData.summary || {};
  const records = attendanceData.records || [];

  const getStatusBadge = (status) => {
    const statusColors = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      late: 'bg-amber-100 text-amber-800',
      half_day: 'bg-blue-100 text-blue-800',
      excused: 'bg-purple-100 text-purple-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <DashboardLayout title="My Attendance">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Attendance">
      <div className="space-y-6">
        {/* Month Selector */}
        <div className="flex items-center gap-4">
          <Label>Select Month:</Label>
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-48"
          />
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{summary.present || 0}</p>
              <p className="text-sm text-muted-foreground">Present</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{summary.absent || 0}</p>
              <p className="text-sm text-muted-foreground">Absent</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{summary.late || 0}</p>
              <p className="text-sm text-muted-foreground">Late</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{summary.half_day || 0}</p>
              <p className="text-sm text-muted-foreground">Half Day</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-primary">{summary.attendance_percentage || summary.percentage || 0}%</p>
              <p className="text-sm text-muted-foreground">Attendance %</p>
            </CardContent>
          </Card>
        </div>

        {/* Attendance Records */}
        <Card>
          <CardHeader>
            <CardTitle>Attendance Records</CardTitle>
            <CardDescription>Your attendance for {selectedMonth}</CardDescription>
          </CardHeader>
          <CardContent>
            {records.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No attendance records for this month</p>
            ) : (
              <div className="space-y-2">
                {records.map((record, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{new Date(record.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}</p>
                      {record.remarks && <p className="text-sm text-muted-foreground">{record.remarks}</p>}
                    </div>
                    <Badge className={getStatusBadge(record.status)}>
                      {record.status?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const STUDENT_STATUS = [
  { value: 'present', label: 'Present', color: 'bg-green-100 text-green-800' },
  { value: 'absent', label: 'Absent', color: 'bg-red-100 text-red-800' },
  { value: 'late', label: 'Late', color: 'bg-amber-100 text-amber-800' },
  { value: 'half_day', label: 'Half Day', color: 'bg-blue-100 text-blue-800' },
  { value: 'excused', label: 'Excused', color: 'bg-purple-100 text-purple-800' }
];

const STAFF_STATUS = [
  { value: 'present', label: 'Present', color: 'bg-green-100 text-green-800' },
  { value: 'absent', label: 'Absent', color: 'bg-red-100 text-red-800' },
  { value: 'half_day', label: 'Half Day', color: 'bg-blue-100 text-blue-800' },
  { value: 'on_leave', label: 'On Leave', color: 'bg-purple-100 text-purple-800' },
  { value: 'wfh', label: 'WFH', color: 'bg-cyan-100 text-cyan-800' }
];

const AttendancePageV2Admin = () => {
  const { getAuthHeader, user } = useAuth();
  const [activeTab, setActiveTab] = useState('student');
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Data
  const [classes, setClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [settings, setSettings] = useState(null);
  
  // Staff Monthly Report
  const [staffReportDialog, setStaffReportDialog] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [staffReport, setStaffReport] = useState(null);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Reports Tab State
  const [classReport, setClassReport] = useState(null);
  const [staffMonthlyReport, setStaffMonthlyReport] = useState(null);
  const [reportClassId, setReportClassId] = useState('');
  const [reportStaffType, setReportStaffType] = useState('all');
  const [reportLoading, setReportLoading] = useState(false);
  
  // Student Attendance
  const [selectedClass, setSelectedClass] = useState('');
  const [attendanceType, setAttendanceType] = useState('daily');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [classView, setClassView] = useState(null);
  const [studentRecords, setStudentRecords] = useState({});
  
  // Staff Attendance
  const [staffView, setStaffView] = useState(null);
  const [staffRecords, setStaffRecords] = useState({});
  
  // Settings dialog
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    school_start_time: '08:00',
    school_end_time: '14:00',
    late_threshold_minutes: 15,
    low_attendance_threshold: 75,
    send_low_attendance_alert: true
  });
  
  // Leave Approval State
  const [leaves, setLeaves] = useState([]);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass && activeTab === 'student') {
      fetchClassAttendance();
    }
  }, [selectedClass, selectedDate, attendanceType, selectedPeriod]);

  useEffect(() => {
    if (activeTab === 'staff') {
      fetchStaffAttendance();
    }
  }, [selectedDate, activeTab]);
  
  useEffect(() => {
    if (activeTab === 'leaves' && isAdmin) {
      fetchLeaves();
    }
  }, [activeTab]);

  const fetchInitialData = async () => {
    try {
      const [classesRes, periodsRes, subjectsRes, settingsRes, teachersRes] = await Promise.all([
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/timetable/periods`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/timetable/subjects`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/attendance/settings`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() })
      ]);
      setClasses(classesRes.data);
      setPeriods(periodsRes.data.filter(p => p.period_type === 'class'));
      setSubjects(subjectsRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
      setTeachers(teachersRes.data.items || teachersRes.data || []);
      
      if (classesRes.data.length > 0) {
        setSelectedClass(classesRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch initial data');
    }
  };
  
  // Leave Management Functions
  const fetchLeaves = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/attendance/leaves?status=pending`, { headers: getAuthHeader() });
      setLeaves(res.data);
    } catch (error) {
      console.error('Failed to fetch leaves');
    }
  };
  
  const approveLeave = async (leaveId) => {
    try {
      await axios.patch(`${API_URL}/api/attendance/leaves/${leaveId}/approve`, {}, { headers: getAuthHeader() });
      toast.success('Leave approved');
      fetchLeaves();
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };
  
  const rejectLeave = async (leaveId) => {
    try {
      await axios.patch(`${API_URL}/api/attendance/leaves/${leaveId}/reject`, {}, { headers: getAuthHeader() });
      toast.success('Leave rejected');
      fetchLeaves();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const fetchClassAttendance = async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/attendance/student/class-view/${selectedClass}?date=${selectedDate}&attendance_type=${attendanceType}`;
      if (attendanceType === 'period' && selectedPeriod) {
        url += `&period_id=${selectedPeriod}`;
      }
      const res = await axios.get(url, { headers: getAuthHeader() });
      setClassView(res.data);
      
      // Initialize records
      const records = {};
      res.data.students.forEach(s => {
        records[s.student_id] = {
          status: s.status || 'present',
          remarks: s.remarks || ''
        };
      });
      setStudentRecords(records);
    } catch (error) {
      console.error('Failed to fetch class attendance');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffMonthlyReport = async (staffId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/attendance/staff/report/${staffId}?month=${reportMonth}`,
        { headers: getAuthHeader() }
      );
      setStaffReport(res.data);
    } catch (error) {
      console.error('Failed to fetch staff report');
      toast.error('Failed to fetch report');
    }
  };

  const openStaffReport = (staffId) => {
    setSelectedStaffId(staffId);
    setStaffReportDialog(true);
    fetchStaffMonthlyReport(staffId);
  };

  // Fetch Class-wise Attendance Report
  const fetchClassReport = async () => {
    if (!reportClassId) {
      toast.error('Please select a class');
      return;
    }
    setReportLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/attendance/student/class-report/${reportClassId}?month=${reportMonth}`,
        { headers: getAuthHeader() }
      );
      setClassReport(res.data);
    } catch (error) {
      console.error('Failed to fetch class report:', error);
      toast.error('Failed to fetch class report');
    } finally {
      setReportLoading(false);
    }
  };

  // Fetch Staff Monthly Report (All Staff)
  const fetchAllStaffReport = async () => {
    setReportLoading(true);
    try {
      const typeParam = reportStaffType !== 'all' ? `&staff_type=${reportStaffType}` : '';
      const res = await axios.get(
        `${API_URL}/api/attendance/staff/monthly-report?month=${reportMonth}${typeParam}`,
        { headers: getAuthHeader() }
      );
      setStaffMonthlyReport(res.data);
    } catch (error) {
      console.error('Failed to fetch staff report:', error);
      toast.error('Failed to fetch staff report');
    } finally {
      setReportLoading(false);
    }
  };

  const fetchStaffAttendance = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/attendance/staff/daily-view?date=${selectedDate}`, { headers: getAuthHeader() });
      setStaffView(res.data);
      
      // Initialize records
      const records = {};
      [...res.data.teachers, ...res.data.non_teaching_staff].forEach(s => {
        records[s.staff_id] = {
          status: s.status || 'present',
          check_in_time: s.check_in_time || '',
          check_out_time: s.check_out_time || '',
          remarks: ''
        };
      });
      setStaffRecords(records);
    } catch (error) {
      console.error('Failed to fetch staff attendance');
    } finally {
      setLoading(false);
    }
  };

  const saveStudentAttendance = async () => {
    try {
      const records = Object.entries(studentRecords).map(([student_id, data]) => ({
        student_id,
        status: data.status,
        remarks: data.remarks
      }));
      
      await axios.post(`${API_URL}/api/attendance/student/bulk`, {
        class_id: selectedClass,
        date: selectedDate,
        attendance_type: attendanceType,
        period_id: attendanceType === 'period' ? selectedPeriod : null,
        records
      }, { headers: getAuthHeader() });
      
      toast.success('Student attendance saved!');
      fetchClassAttendance();
    } catch (error) {
      toast.error('Failed to save attendance');
    }
  };

  const saveStaffAttendance = async () => {
    try {
      const records = Object.entries(staffRecords).map(([staff_id, data]) => ({
        staff_id,
        staff_type: staffView?.teachers.find(t => t.staff_id === staff_id) ? 'teacher' : 'staff',
        status: data.status,
        check_in_time: data.check_in_time || null,
        check_out_time: data.check_out_time || null,
        remarks: data.remarks
      }));
      
      await axios.post(`${API_URL}/api/attendance/staff/bulk`, {
        date: selectedDate,
        records
      }, { headers: getAuthHeader() });
      
      toast.success('Staff attendance saved!');
      fetchStaffAttendance();
    } catch (error) {
      toast.error('Failed to save attendance');
    }
  };

  const saveSettings = async () => {
    try {
      await axios.post(`${API_URL}/api/attendance/settings`, settingsForm, { headers: getAuthHeader() });
      toast.success('Settings saved!');
      setSettingsDialog(false);
      setSettings(settingsForm);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const markAllPresent = (type) => {
    if (type === 'student') {
      const updated = { ...studentRecords };
      Object.keys(updated).forEach(id => {
        updated[id].status = 'present';
      });
      setStudentRecords(updated);
    } else {
      const updated = { ...staffRecords };
      Object.keys(updated).forEach(id => {
        updated[id].status = 'present';
      });
      setStaffRecords(updated);
    }
  };

  const markAllAbsent = (type) => {
    if (type === 'student') {
      const updated = { ...studentRecords };
      Object.keys(updated).forEach(id => {
        updated[id].status = 'absent';
      });
      setStudentRecords(updated);
    } else {
      const updated = { ...staffRecords };
      Object.keys(updated).forEach(id => {
        updated[id].status = 'absent';
      });
      setStaffRecords(updated);
    }
  };

  const isAdmin = user?.role === 'school_admin' || user?.role === 'super_admin';
  const isTeacher = user?.role === 'teacher';

  return (
    <DashboardLayout title="Attendance Management">
      <div className="space-y-6" data-testid="attendance-page-v2">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <TabsList>
              <TabsTrigger value="student" data-testid="student-tab">
                <Users className="h-4 w-4 mr-1" /> Student
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="staff" data-testid="staff-tab">
                  <UserCheck className="h-4 w-4 mr-1" /> Staff
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="reports" data-testid="reports-tab">
                  <BarChart3 className="h-4 w-4 mr-1" /> Reports
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="leaves" data-testid="leaves-tab">
                  <Clock className="h-4 w-4 mr-1" /> Leave Requests
                  {leaves.filter(l => l.status === 'pending').length > 0 && (
                    <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">
                      {leaves.filter(l => l.status === 'pending').length}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>
            
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto"
                data-testid="date-input"
              />
              {isAdmin && (
                <Button variant="outline" size="icon" onClick={() => setSettingsDialog(true)}>
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Student Attendance Tab */}
          <TabsContent value="student" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Select value={selectedClass} onValueChange={setSelectedClass}>
                      <SelectTrigger className="w-40" data-testid="class-select">
                        <SelectValue placeholder="Select Class" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select value={attendanceType} onValueChange={setAttendanceType}>
                      <SelectTrigger className="w-36" data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="period">Period-wise</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {attendanceType === 'period' && (
                      <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                        <SelectTrigger className="w-40" data-testid="period-select">
                          <SelectValue placeholder="Select Period" />
                        </SelectTrigger>
                        <SelectContent>
                          {periods.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={async () => {
                        try {
                          const month = selectedDate?.slice(0, 7) || new Date().toISOString().slice(0, 7);
                          const response = await axios.get(
                            `${API_URL}/api/attendance/student/export/excel?class_id=${selectedClass || ''}&month=${month}`,
                            { headers: getAuthHeader(), responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `student_attendance_${month}.xlsx`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('Attendance exported!');
                        } catch (error) {
                          toast.error('Export failed');
                        }
                      }}
                      data-testid="export-student-attendance-btn"
                    >
                      <Calendar className="h-4 w-4 mr-1" /> Export Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => markAllPresent('student')}>
                      <CheckCircle className="h-4 w-4 mr-1 text-green-600" /> All Present
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => markAllAbsent('student')}>
                      <XCircle className="h-4 w-4 mr-1 text-red-600" /> All Absent
                    </Button>
                    <Button onClick={saveStudentAttendance} data-testid="save-student-btn">
                      <Save className="h-4 w-4 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {classView && (
                  <>
                    {/* Summary */}
                    <div className="grid grid-cols-5 gap-3 mb-4">
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold">{classView.summary.total}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-green-600">{Object.values(studentRecords).filter(r => r.status === 'present').length}</p>
                        <p className="text-xs text-muted-foreground">Present</p>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-red-600">{Object.values(studentRecords).filter(r => r.status === 'absent').length}</p>
                        <p className="text-xs text-muted-foreground">Absent</p>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-amber-600">{Object.values(studentRecords).filter(r => r.status === 'late').length}</p>
                        <p className="text-xs text-muted-foreground">Late</p>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-blue-600">{classView.summary.not_marked}</p>
                        <p className="text-xs text-muted-foreground">Not Marked</p>
                      </div>
                    </div>

                    {/* Student List */}
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full" data-testid="student-attendance-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="p-3 text-left text-sm">Roll No</th>
                            <th className="p-3 text-left text-sm">Name</th>
                            <th className="p-3 text-center text-sm">Status</th>
                            <th className="p-3 text-left text-sm">Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classView.students.map(student => (
                            <tr key={student.student_id} className="border-t">
                              <td className="p-3 font-medium">{student.roll_number}</td>
                              <td className="p-3">{student.name}</td>
                              <td className="p-3">
                                <div className="flex justify-center gap-1">
                                  {STUDENT_STATUS.map(status => (
                                    <button
                                      key={status.value}
                                      onClick={() => setStudentRecords(prev => ({
                                        ...prev,
                                        [student.student_id]: { ...prev[student.student_id], status: status.value }
                                      }))}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                                        studentRecords[student.student_id]?.status === status.value
                                          ? status.color + ' ring-2 ring-offset-1'
                                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                      }`}
                                      data-testid={`status-${student.student_id}-${status.value}`}
                                    >
                                      {status.label.charAt(0)}
                                    </button>
                                  ))}
                                </div>
                              </td>
                              <td className="p-3">
                                <Input
                                  value={studentRecords[student.student_id]?.remarks || ''}
                                  onChange={(e) => setStudentRecords(prev => ({
                                    ...prev,
                                    [student.student_id]: { ...prev[student.student_id], remarks: e.target.value }
                                  }))}
                                  placeholder="Remarks"
                                  className="h-8 text-sm"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {classView.students.length === 0 && (
                        <p className="text-center text-muted-foreground py-8">No students in this class</p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Attendance Tab */}
          <TabsContent value="staff" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Staff Attendance - {selectedDate}</CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={async () => {
                        try {
                          const month = selectedDate?.slice(0, 7) || new Date().toISOString().slice(0, 7);
                          const response = await axios.get(
                            `${API_URL}/api/attendance/staff/export/excel?month=${month}`,
                            { headers: getAuthHeader(), responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `staff_attendance_${month}.xlsx`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('Staff attendance exported!');
                        } catch (error) {
                          toast.error('Export failed');
                        }
                      }}
                      data-testid="export-staff-attendance-btn"
                    >
                      <Calendar className="h-4 w-4 mr-1" /> Export Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => markAllPresent('staff')}>
                      <CheckCircle className="h-4 w-4 mr-1 text-green-600" /> All Present
                    </Button>
                    <Button onClick={saveStaffAttendance} data-testid="save-staff-btn">
                      <Save className="h-4 w-4 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {staffView && (
                  <>
                    {/* Summary */}
                    <div className="grid grid-cols-5 gap-3 mb-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-blue-600">{staffView.summary.total_teachers}</p>
                        <p className="text-xs text-muted-foreground">Teachers</p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-purple-600">{staffView.summary.total_staff}</p>
                        <p className="text-xs text-muted-foreground">Staff</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-green-600">{staffView.summary.teachers_present + staffView.summary.staff_present}</p>
                        <p className="text-xs text-muted-foreground">Present</p>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-amber-600">{staffView.summary.late_arrivals}</p>
                        <p className="text-xs text-muted-foreground">Late</p>
                      </div>
                      <div className="bg-cyan-50 dark:bg-cyan-900/20 p-3 rounded-lg text-center">
                        <p className="text-xl font-bold text-cyan-600">{Math.round(staffView.summary.total_overtime_minutes / 60)}h</p>
                        <p className="text-xs text-muted-foreground">Overtime</p>
                      </div>
                    </div>

                    {/* Teachers */}
                    <h3 className="font-medium mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" /> Teaching Staff ({staffView.teachers.length})
                    </h3>
                    <div className="border rounded-lg overflow-hidden mb-6">
                      <table className="w-full" data-testid="teacher-attendance-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="p-3 text-left text-sm w-[200px]">Name</th>
                            <th className="p-3 text-left text-sm w-[150px]">Designation</th>
                            <th className="p-3 text-center text-sm w-[130px]">Status</th>
                            <th className="p-3 text-center text-sm w-[140px]">Check In</th>
                            <th className="p-3 text-center text-sm w-[140px]">Check Out</th>
                            <th className="p-3 text-center text-sm w-[150px]">Late/OT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {staffView.teachers.map(staff => (
                            <tr key={staff.staff_id} className="border-t">
                              <td className="p-3 font-medium">
                                <button 
                                  onClick={() => openStaffReport(staff.staff_id)}
                                  className="hover:text-primary hover:underline text-left"
                                >
                                  {staff.name}
                                </button>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{staff.designation}</td>
                              <td className="p-3">
                                <Select
                                  value={staffRecords[staff.staff_id]?.status || 'present'}
                                  onValueChange={(v) => setStaffRecords(prev => ({
                                    ...prev,
                                    [staff.staff_id]: { ...prev[staff.staff_id], status: v }
                                  }))}
                                >
                                  <SelectTrigger className="h-8 w-28 mx-auto">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STAFF_STATUS.map(s => (
                                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-3 text-center">
                                <Input
                                  type="time"
                                  value={staffRecords[staff.staff_id]?.check_in_time || ''}
                                  onChange={(e) => setStaffRecords(prev => ({
                                    ...prev,
                                    [staff.staff_id]: { ...prev[staff.staff_id], check_in_time: e.target.value }
                                  }))}
                                  className="h-8 w-32 mx-auto"
                                  data-testid={`ts-checkin-${staff.staff_id}`}
                                />
                              </td>
                              <td className="p-3 text-center">
                                <Input
                                  type="time"
                                  value={staffRecords[staff.staff_id]?.check_out_time || ''}
                                  onChange={(e) => setStaffRecords(prev => ({
                                    ...prev,
                                    [staff.staff_id]: { ...prev[staff.staff_id], check_out_time: e.target.value }
                                  }))}
                                  className="h-8 w-32 mx-auto"
                                  data-testid={`ts-checkout-${staff.staff_id}`}
                                />
                              </td>
                              <td className="p-3 text-center">
                                {staff.is_late && (
                                  <Badge variant="destructive" className="text-xs">Late {staff.late_minutes}m</Badge>
                                )}
                                {staff.overtime_minutes > 0 && (
                                  <Badge variant="secondary" className="text-xs ml-1">OT {staff.overtime_minutes}m</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Non-Teaching Staff */}
                    <h3 className="font-medium mb-2 flex items-center gap-2">
                      <UserCheck className="h-4 w-4" /> Non-Teaching Staff ({staffView.non_teaching_staff.length})
                    </h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full" data-testid="staff-attendance-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="p-3 text-left text-sm w-[200px]">Name</th>
                            <th className="p-3 text-left text-sm w-[150px]">Designation</th>
                            <th className="p-3 text-center text-sm w-[130px]">Status</th>
                            <th className="p-3 text-center text-sm w-[140px]">Check In</th>
                            <th className="p-3 text-center text-sm w-[140px]">Check Out</th>
                            <th className="p-3 text-center text-sm w-[150px]">Late/OT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {staffView.non_teaching_staff.map(staff => (
                            <tr key={staff.staff_id} className="border-t">
                              <td className="p-3 font-medium">
                                <button 
                                  onClick={() => openStaffReport(staff.staff_id)}
                                  className="hover:text-primary hover:underline text-left"
                                >
                                  {staff.name}
                                </button>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{staff.designation}</td>
                              <td className="p-3">
                                <Select
                                  value={staffRecords[staff.staff_id]?.status || 'present'}
                                  onValueChange={(v) => setStaffRecords(prev => ({
                                    ...prev,
                                    [staff.staff_id]: { ...prev[staff.staff_id], status: v }
                                  }))}
                                >
                                  <SelectTrigger className="h-8 w-28 mx-auto">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STAFF_STATUS.map(s => (
                                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-3 text-center">
                                <Input
                                  type="time"
                                  value={staffRecords[staff.staff_id]?.check_in_time || ''}
                                  onChange={(e) => setStaffRecords(prev => ({
                                    ...prev,
                                    [staff.staff_id]: { ...prev[staff.staff_id], check_in_time: e.target.value }
                                  }))}
                                  className="h-8 w-32 mx-auto"
                                  data-testid={`nts-checkin-${staff.staff_id}`}
                                />
                              </td>
                              <td className="p-3 text-center">
                                <Input
                                  type="time"
                                  value={staffRecords[staff.staff_id]?.check_out_time || ''}
                                  onChange={(e) => setStaffRecords(prev => ({
                                    ...prev,
                                    [staff.staff_id]: { ...prev[staff.staff_id], check_out_time: e.target.value }
                                  }))}
                                  className="h-8 w-32 mx-auto"
                                  data-testid={`nts-checkout-${staff.staff_id}`}
                                />
                              </td>
                              <td className="p-3 text-center">
                                {staff.is_late && (
                                  <Badge variant="destructive" className="text-xs">Late {staff.late_minutes}m</Badge>
                                )}
                                {staff.overtime_minutes > 0 && (
                                  <Badge variant="secondary" className="text-xs ml-1">OT {staff.overtime_minutes}m</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {staffView.non_teaching_staff.length === 0 && (
                        <p className="text-center text-muted-foreground py-4">No non-teaching staff</p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Reports Tab */}
          {isAdmin && (
            <TabsContent value="reports" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Class-wise Student Report */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-5 w-5" /> Class-wise Student Attendance Report
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Select value={reportClassId} onValueChange={setReportClassId}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select Class" />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name} {c.section}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        type="month" 
                        value={reportMonth} 
                        onChange={(e) => setReportMonth(e.target.value)}
                        className="w-40"
                      />
                      <Button onClick={fetchClassReport} disabled={reportLoading}>
                        {reportLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate'}
                      </Button>
                    </div>
                    
                    {classReport && (
                      <div className="space-y-3">
                        <div className="bg-muted p-3 rounded-lg">
                          <h4 className="font-semibold">{classReport.class_name} {classReport.section} - {classReport.month}</h4>
                          <div className="grid grid-cols-4 gap-2 mt-2 text-sm">
                            <div>Students: <span className="font-bold">{classReport.summary?.total_students}</span></div>
                            <div>Present Days: <span className="font-bold text-green-600">{classReport.summary?.total_present}</span></div>
                            <div>Absent Days: <span className="font-bold text-red-600">{classReport.summary?.total_absent}</span></div>
                            <div>Avg %: <span className="font-bold text-blue-600">{classReport.summary?.average_attendance}%</span></div>
                          </div>
                        </div>
                        
                        <div className="max-h-80 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white">
                              <tr className="border-b">
                                <th className="text-left p-2">Roll</th>
                                <th className="text-left p-2">Name</th>
                                <th className="text-center p-2">P</th>
                                <th className="text-center p-2">A</th>
                                <th className="text-center p-2">L</th>
                                <th className="text-center p-2">%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {classReport.students?.map(s => (
                                <tr key={s.student_id} className="border-b hover:bg-muted/50">
                                  <td className="p-2">{s.roll_number || '-'}</td>
                                  <td className="p-2">{s.student_name}</td>
                                  <td className="text-center p-2 text-green-600">{s.present}</td>
                                  <td className="text-center p-2 text-red-600">{s.absent}</td>
                                  <td className="text-center p-2 text-yellow-600">{s.late}</td>
                                  <td className="text-center p-2 font-medium">{s.attendance_percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Staff Monthly Report */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserCheck className="h-5 w-5" /> Staff Monthly Attendance Report
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Select value={reportStaffType} onValueChange={setReportStaffType}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Staff Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Staff</SelectItem>
                          <SelectItem value="teacher">Teachers</SelectItem>
                          <SelectItem value="staff">Non-Teaching</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input 
                        type="month" 
                        value={reportMonth} 
                        onChange={(e) => setReportMonth(e.target.value)}
                        className="w-40"
                      />
                      <Button onClick={fetchAllStaffReport} disabled={reportLoading}>
                        {reportLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate'}
                      </Button>
                    </div>
                    
                    {staffMonthlyReport && (
                      <div className="space-y-3">
                        <div className="bg-muted p-3 rounded-lg">
                          <h4 className="font-semibold">Staff Report - {staffMonthlyReport.month}</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                            <div>Staff: <span className="font-bold">{staffMonthlyReport.summary?.total_staff}</span></div>
                            <div>Present: <span className="font-bold text-green-600">{staffMonthlyReport.summary?.total_present_days}</span></div>
                            <div>Late Min: <span className="font-bold text-yellow-600">{staffMonthlyReport.summary?.total_late_minutes}</span></div>
                            <div>OT Min: <span className="font-bold text-blue-600">{staffMonthlyReport.summary?.total_overtime_minutes}</span></div>
                          </div>
                        </div>
                        
                        <div className="max-h-80 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white">
                              <tr className="border-b">
                                <th className="text-left p-2">Name</th>
                                <th className="text-center p-2">Type</th>
                                <th className="text-center p-2">P</th>
                                <th className="text-center p-2">A</th>
                                <th className="text-center p-2">Late</th>
                                <th className="text-center p-2">OT</th>
                                <th className="text-center p-2">%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {staffMonthlyReport.staff?.map(s => (
                                <tr key={s.staff_id} className="border-b hover:bg-muted/50">
                                  <td className="p-2">{s.staff_name}</td>
                                  <td className="text-center p-2">
                                    <Badge variant={s.staff_type === 'teacher' ? 'default' : 'secondary'} className="text-xs">
                                      {s.staff_type}
                                    </Badge>
                                  </td>
                                  <td className="text-center p-2 text-green-600">{s.present}</td>
                                  <td className="text-center p-2 text-red-600">{s.absent}</td>
                                  <td className="text-center p-2 text-yellow-600">{s.late_minutes}m</td>
                                  <td className="text-center p-2 text-blue-600">{s.overtime_minutes}m</td>
                                  <td className="text-center p-2 font-medium">{s.attendance_percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
          
          {/* Leave Requests Tab */}
          {isAdmin && (
            <TabsContent value="leaves" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-5 w-5" /> Pending Leave Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {leaves.filter(l => l.status === 'pending').length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No pending leave requests</p>
                  ) : (
                    <div className="space-y-3">
                      {leaves.filter(l => l.status === 'pending').map(leave => {
                        return (
                          <div key={leave.id} className="border rounded-lg p-4 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{leave.staff_name || 'Staff Member'}</p>
                              <p className="text-sm text-muted-foreground">
                                {leave.leave_type?.toUpperCase()} • {leave.start_date} to {leave.end_date} ({leave.days} days)
                              </p>
                              <p className="text-sm mt-1">{leave.reason}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="text-green-600 border-green-600" onClick={() => approveLeave(leave.id)}>
                                <CheckCircle className="h-4 w-4 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-600" onClick={() => rejectLeave(leave.id)}>
                                <XCircle className="h-4 w-4 mr-1" /> Reject
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attendance Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>School Start Time</Label>
                <Input
                  type="time"
                  value={settingsForm.school_start_time}
                  onChange={(e) => setSettingsForm(p => ({ ...p, school_start_time: e.target.value }))}
                />
              </div>
              <div>
                <Label>School End Time</Label>
                <Input
                  type="time"
                  value={settingsForm.school_end_time}
                  onChange={(e) => setSettingsForm(p => ({ ...p, school_end_time: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Late Threshold (minutes after start)</Label>
              <Input
                type="number"
                value={settingsForm.late_threshold_minutes}
                onChange={(e) => setSettingsForm(p => ({ ...p, late_threshold_minutes: parseInt(e.target.value) }))}
              />
            </div>
            <div>
              <Label>Low Attendance Alert Threshold (%)</Label>
              <Input
                type="number"
                value={settingsForm.low_attendance_threshold}
                onChange={(e) => setSettingsForm(p => ({ ...p, low_attendance_threshold: parseFloat(e.target.value) }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={settingsForm.send_low_attendance_alert}
                onCheckedChange={(c) => setSettingsForm(p => ({ ...p, send_low_attendance_alert: c }))}
              />
              <Label>Send in-app notification when attendance drops below threshold</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialog(false)}>Cancel</Button>
            <Button onClick={saveSettings}>Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Monthly Report Dialog */}
      <Dialog open={staffReportDialog} onOpenChange={setStaffReportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Monthly Attendance Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Month Selector */}
            <div className="flex items-center gap-2">
              <Label>Month:</Label>
              <Input
                type="month"
                value={reportMonth}
                onChange={(e) => {
                  setReportMonth(e.target.value);
                  if (selectedStaffId) fetchStaffMonthlyReport(selectedStaffId);
                }}
                className="w-40"
              />
            </div>

            {staffReport && (
              <>
                {/* Staff Info */}
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-semibold text-lg">{staffReport.staff_name}</p>
                  <p className="text-sm text-muted-foreground">{staffReport.designation}</p>
                </div>

                {/* Attendance Summary */}
                <div className="grid grid-cols-5 gap-2">
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <p className="text-xl font-bold text-green-600">{staffReport.summary?.present || 0}</p>
                    <p className="text-xs">Present</p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg text-center">
                    <p className="text-xl font-bold text-red-600">{staffReport.summary?.absent || 0}</p>
                    <p className="text-xs">Absent</p>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <p className="text-xl font-bold text-amber-600">{staffReport.summary?.late_days || 0}</p>
                    <p className="text-xs">Late Days</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-xl font-bold text-blue-600">{staffReport.summary?.half_day || 0}</p>
                    <p className="text-xs">Half Days</p>
                  </div>
                  <div className="bg-cyan-50 p-3 rounded-lg text-center">
                    <p className="text-xl font-bold text-cyan-600">
                      {Math.round((staffReport.summary?.total_overtime_minutes || 0) / 60)}h
                    </p>
                    <p className="text-xs">Overtime</p>
                  </div>
                </div>

                {/* Deduction Summary */}
                {staffReport.deduction_summary && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      💰 Salary Impact Summary
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Excess Late Days:</span>
                          <span className="font-medium">{staffReport.deduction_summary.excess_late_days}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Late Deduction:</span>
                          <span className="font-medium text-red-600">-₹{staffReport.deduction_summary.late_deduction}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Late → Half Days:</span>
                          <span className="font-medium">{staffReport.deduction_summary.late_converted_to_half_days}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Overtime Hours:</span>
                          <span className="font-medium">{staffReport.deduction_summary.overtime_hours}h</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">OT Earnings:</span>
                          <span className="font-medium text-green-600">+₹{staffReport.deduction_summary.overtime_earnings}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Settings Applied */}
                    <div className="mt-4 pt-3 border-t text-xs text-muted-foreground">
                      <p className="font-medium mb-1">Settings Applied:</p>
                      <div className="flex flex-wrap gap-3">
                        <span>Late Deduction: ₹{staffReport.deduction_summary.settings_applied?.late_deduction_per_day}/day</span>
                        <span>•</span>
                        <span>Max Late Allowed: {staffReport.deduction_summary.settings_applied?.max_late_days_allowed} days</span>
                        <span>•</span>
                        <span>OT Rate: ₹{staffReport.deduction_summary.settings_applied?.overtime_rate_per_hour}/hr</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStaffReportDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Main Component with Role-based Routing
const AttendancePageV2Wrapper = () => {
  const { getAuthHeader, user } = useAuth();
  
  // Student gets simplified view
  if (user?.role === 'student') {
    return <StudentAttendanceView getAuthHeader={getAuthHeader} />;
  }
  
  // Admin/Teacher gets full view
  return <AttendancePageV2Admin />;
};

export default AttendancePageV2Wrapper;
